import { useState } from 'react';
import { Lock, Bell, User, ArrowLeft, Sun, Moon, Palette } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useClientSettings, ThemeMode } from '../client-pwa/settings';

const modes: { key: ThemeMode; label: string; icon: any }[] = [
  { key: 'light', label: 'Light', icon: Sun },
  { key: 'dark', label: 'Dark', icon: Moon },
];

export default function SettingsPageV3() {
  const { settings, update, themeMode, setThemeMode } = useClientSettings();
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaved, setPwdSaved] = useState(false);

  const handleSavePwd = (e: React.FormEvent) => {
    e.preventDefault();
    setPwdSaved(true);
    setPwd({ current: '', next: '', confirm: '' });
    setTimeout(() => setPwdSaved(false), 2000);
  };

  return (
    <div className="space-y-6 px-5 pt-6 pb-12">
      <div className="flex items-center gap-3">
        <Link to="/client-pwa-v3" className="flex h-9 w-9 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:border-stone-500">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <p className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">Preferences</p>
          <h1 className="font-serif text-2xl font-bold tracking-tight">Settings</h1>
        </div>
      </div>

      <Section icon={User} title="Account">
        <Row label="Name" value="James Halloran" />
        <Row label="Email" value="james@jamestiling.com.au" />
        <Row label="Company" value="James Tiling Pty Ltd" />
        <Row label="ACN" value="624 587 192" />
      </Section>

      <Section icon={Palette} title="Appearance">
        <div className="grid grid-cols-2 gap-0 divide-x divide-stone-200 dark:divide-stone-800">
          {modes.map(m => {
            const Icon = m.icon;
            const isActive = themeMode === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setThemeMode(m.key)}
                className={`flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-stone-900 dark:bg-stone-100 text-stone-50 dark:text-stone-900'
                    : 'bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{m.label}</span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section icon={Bell} title="Notifications">
        <ToggleRow label="Email notifications" value={settings.emailNotifications} onChange={v => update('emailNotifications', v)} />
        <Divider />
        <ToggleRow label="Push notifications" value={settings.pushNotifications} onChange={v => update('pushNotifications', v)} />
      </Section>

      <Section icon={Lock} title="Change password">
        <form onSubmit={handleSavePwd} className="space-y-2.5 px-4 py-4">
          {[
            { v: pwd.current, p: 'Current password', set: (x: string) => setPwd({ ...pwd, current: x }) },
            { v: pwd.next, p: 'New password', set: (x: string) => setPwd({ ...pwd, next: x }) },
            { v: pwd.confirm, p: 'Confirm new password', set: (x: string) => setPwd({ ...pwd, confirm: x }) },
          ].map((f, i) => (
            <input
              key={i}
              type="password"
              value={f.v}
              onChange={e => f.set(e.target.value)}
              placeholder={f.p}
              className="w-full border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2.5 text-sm placeholder:text-stone-400 focus:border-teal-700 focus:outline-none"
            />
          ))}
          <button
            type="submit"
            disabled={!pwd.current || !pwd.next || pwd.next !== pwd.confirm}
            className={`w-full border py-3 font-mono text-[13px] font-bold uppercase tracking-[0.2em] transition-all ${
              pwdSaved
                ? 'border-emerald-700 bg-emerald-700 text-white'
                : 'border-stone-900 dark:border-stone-100 bg-stone-900 dark:bg-stone-100 text-stone-50 hover:bg-stone-800 disabled:border-stone-300 disabled:bg-stone-300'
            }`}
          >
            {pwdSaved ? '✓ Saved' : 'Save changes'}
          </button>
        </form>
      </Section>

      <div className="border-t border-stone-200 dark:border-stone-800 pt-6 text-center">
        <p className="font-serif text-[13px] italic text-stone-400 dark:text-stone-500">Compiled with care</p>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400 dark:text-stone-500">
          Powered by <span className="font-bold text-stone-700 dark:text-stone-300">RoniDoc</span> · v3.0
        </p>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-2 flex items-center gap-2 font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">
        <Icon className="h-3 w-3" />
        {title}
      </h2>
      <div className="border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 px-4 py-3 last:border-0">
      <span className="text-sm text-stone-600 dark:text-stone-400">{label}</span>
      <span className="font-mono text-sm font-medium tabular-nums text-stone-900 dark:text-stone-100">{value}</span>
    </div>
  );
}

function Divider() { return <div className="h-px bg-stone-200 dark:bg-stone-800" />; }

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-sm">{label}</span>
      <button
        onClick={() => onChange(!value)}
        className={`relative h-5 w-10 transition-colors ${value ? 'bg-teal-700 dark:bg-teal-500' : 'bg-stone-200 dark:bg-stone-800'}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 bg-white dark:bg-stone-900 transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}
